/**
 * Connection Error Screen
 * Shown when the backend is unreachable or session fails to load.
 * Matches ErrorBoundary styling with better error detection and reporting.
 */

import { useFeedback } from '@/app/providers/FeedbackContext'
import { removeAuthItem } from '@/lib/authStorage'

interface ConnectionErrorScreenProps {
  error: Error
  onRetry: () => void
  onClearCache?: () => void
}

type ErrorType = 'network' | 'timeout' | 'server' | 'auth' | 'unknown'

function detectErrorType(error: Error): ErrorType {
  const message = error.message.toLowerCase()

  // Network errors (offline, DNS, connection refused)
  if (
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('net::') ||
    message.includes('econnrefused') ||
    message.includes('enotfound')
  ) {
    return 'network'
  }

  // Timeout errors
  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('aborted')
  ) {
    return 'timeout'
  }

  // Server errors (5xx)
  if (
    message.includes('502') ||
    message.includes('503') ||
    message.includes('504') ||
    message.includes('500') ||
    message.includes('server error')
  ) {
    return 'server'
  }

  // Auth errors
  if (
    message.includes('401') ||
    message.includes('403') ||
    message.includes('unauthorized') ||
    message.includes('session invalid')
  ) {
    return 'auth'
  }

  return 'unknown'
}

function getErrorContent(errorType: ErrorType): {
  emoji: string
  title: string
  message: string
  suggestion: string
} {
  switch (errorType) {
    case 'network':
      return {
        emoji: '📡',
        title: 'Unable to connect',
        message: "We couldn't reach the server.",
        suggestion: 'Check your internet connection and try again.',
      }
    case 'timeout':
      return {
        emoji: '⏱️',
        title: 'Connection timed out',
        message: 'The server is taking too long to respond.',
        suggestion: 'This might be temporary. Please try again in a moment.',
      }
    case 'server':
      return {
        emoji: '🔧',
        title: 'Server temporarily unavailable',
        message: "We're experiencing some technical difficulties.",
        suggestion: 'Our team has been notified. Please try again shortly.',
      }
    case 'auth':
      return {
        emoji: '🔑',
        title: 'Session expired',
        message: 'Your session could not be verified.',
        suggestion: 'Please sign in again to continue.',
      }
    default:
      return {
        emoji: '😵',
        title: "Couldn't load your session",
        message: 'Something unexpected went wrong.',
        suggestion: 'Try again, or report this issue if it persists.',
      }
  }
}

export function ConnectionErrorScreen({ error, onRetry, onClearCache }: ConnectionErrorScreenProps) {
  const { openWithContext } = useFeedback()

  const errorType = detectErrorType(error)
  const content = getErrorContent(errorType)

  const handleReport = () => {
    openWithContext('bug_report', {
      errorInfo: {
        message: `${content.title}: ${error.message}`,
        stack: `Error Type: ${errorType}\nOriginal Error: ${error.stack || error.message}`,
      },
    })
  }

  const handleClearCache = () => {
    if (onClearCache) {
      onClearCache()
    } else {
      // Default clear cache behavior
      sessionStorage.clear()
      localStorage.removeItem('xavior_auth_token')
      localStorage.removeItem('xavior_guest_id')
      removeAuthItem('xavior_selected_container')
      localStorage.removeItem('xavior_conversation_order')
      window.location.reload()
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: '#f8f9fc' }}>
      <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center border border-gray-100" style={{ maxWidth: '420px', boxShadow: '0 2px 24px rgba(0,0,0,0.06)' }}>
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0ea5e9' }}>
            <span className="text-white text-sm font-bold">X</span>
          </div>
          <span className="text-lg font-medium text-gray-900" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>XAVIOR</span>
        </div>

        <div className="text-4xl mb-4">{content.emoji}</div>
        <h2 className="text-2xl font-normal text-gray-900 mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>
          {content.title}
        </h2>
        <p className="text-sm text-gray-500 mb-1">
          {content.message}
        </p>
        <p className="text-sm text-gray-400 mb-8">
          {content.suggestion}
        </p>

        <div className="flex flex-col gap-3 justify-center">
          <button
            onClick={onRetry}
            className="w-full px-4 py-3 text-white font-medium rounded-xl transition-all text-sm"
            style={{ background: '#0ea5e9', boxShadow: '0 1px 3px rgba(14,165,233,0.3)' }}
            onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#0284c7'; (e.target as HTMLElement).style.transform = 'translateY(-1px)'; (e.target as HTMLElement).style.boxShadow = '0 4px 12px rgba(14,165,233,0.3)' }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.background = '#0ea5e9'; (e.target as HTMLElement).style.transform = 'translateY(0)'; (e.target as HTMLElement).style.boxShadow = '0 1px 3px rgba(14,165,233,0.3)' }}
          >
            Try again
          </button>
          <div className="flex flex-row gap-3 justify-center">
            <button
              onClick={handleClearCache}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all text-sm"
            >
              Clear cache
            </button>
            <button
              onClick={handleReport}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all text-sm"
            >
              Report issue
            </button>
          </div>
        </div>

        <details className="mt-8 text-left">
          <summary className="text-xs text-gray-300 cursor-pointer hover:text-gray-500 transition-colors">
            Technical details
          </summary>
          <pre className="mt-2 p-3 bg-gray-50 rounded-xl text-xs text-gray-400 overflow-auto max-h-40 border border-gray-100">
            {error.stack || error.message}
          </pre>
        </details>
      </div>
    </div>
  )
}
